import { formatDistanceToNow } from "date-fns";
import type { AgenticLogicBindingFieldsProps } from "./AgenticLogicBindingFieldsProps";

import {
  IgAlert,
  IgButton,
  IgChip,
  IgLink,
  IgSelect,
  IgTypography,
} from "@intelligems/ig-theme";
import type { AgenticLogicPlacementDto } from "@intelligems/ig-types";
import { stripMigrationProvenanceMarker } from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";

import { useAgenticLogicAssignments } from "@src/api/agentic-logic/list-agentic-logic-assignments";
import { useAgenticLogicPlacements } from "@src/api/agentic-logic/list-agentic-logic-placements";
import { useAgenticLogicRules } from "@src/api/agentic-logic/list-agentic-logic-rules";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useDismissAlert } from "@src/hooks/useDismissAlert";
import { ASSIGNMENTS_TAB_PATH } from "./assignmentsTabPath";
import { useSelector } from "@src/store";
import { getIsAlertDismissed } from "@src/store/slices/appSettings";

import { buildAgenticLogicRuleOptions } from "./agenticLogicRuleOptions";
import {
  type PlacementLivenessStatus,
  readPlacementLiveness,
} from "./placementLiveness";

const NONE = "";
const RULES_MOVED_ALERT_ID = "agentic-logic-rules-moved";
/**
 * The "+ New rule" row's value. It is an option in the same list rather than a
 * button beside it because that is where an author looks when the rule they
 * want is not there — but it selects nothing, so it is filtered out before the
 * change reaches `onRuleChange`.
 */
const CREATE_RULE_OPTION = "__create__";

/**
 * The write side of a surface's Agentic Logic binding, shared by every
 * container that has one — a checkout block, a post-purchase block, an onsite
 * upsell component.
 *
 * Only the RULE is chosen here. The placement and the (placement, variation)
 * assignment are derived by the backend when the experience is saved, so
 * picking a rule writes nothing on its own and abandoning the editor cannot
 * leave an orphaned placement behind. Everything the assignment itself carries
 * — its budget, the experience it belongs to — is shown read-only and edited
 * on the Assignments tab, which owns it.
 *
 * A rule selection is mandatory: there is no "no rule" option, so a container
 * either serves a rule or is not bound at all.
 *
 * There is no empty-result picker any more either. What a slot does when its
 * rule returns nothing is one of the RULE's settings, beside how many products
 * it returns and which ones it refuses — so it is chosen once, on the rule,
 * rather than restated on every container the rule fills.
 */
export const AgenticLogicBindingFields = ({
  placementId,
  ruleId,
  variationId,
  surface,
  onRuleChange,
  onCreateRule,
  onEditRule,
}: AgenticLogicBindingFieldsProps) => {
  const org = useGetOrg();
  const dismissAlert = useDismissAlert();
  const isRulesMovedAlertDismissed = useSelector(
    getIsAlertDismissed(org.data?.org?.id, RULES_MOVED_ALERT_ID)
  );
  // `isLoading`, not `isFetching`: the rule list revalidates on every mount and
  // window focus, so reading `isFetching` here would disable a populated
  // dropdown — and blink its empty-state helper off — on every open and every
  // tab-back. The control stays usable while the list revalidates and is
  // disabled only while there is nothing to show.
  const { data, isLoading, isError } = useAgenticLogicRules();
  const placements = useAgenticLogicPlacements();
  const assignments = useAgenticLogicAssignments();

  const selected = ruleId ?? NONE;
  const { selectable: selectableRules, selectedIsUnavailable } =
    buildAgenticLogicRuleOptions(data?.rules ?? [], surface, ruleId);
  // The rule's own plain-language description, shown beside the picker so an
  // author can confirm what the selection does without opening the builder.
  const selectedRuleSummary = ruleId
    ? stripMigrationProvenanceMarker(
        data?.rules.find((rule) => rule.id === ruleId)?.description
      ) || undefined
    : undefined;

  const placement: AgenticLogicPlacementDto | undefined = placementId
    ? placements.data?.placements.find((p) => p.id === placementId)
    : undefined;

  // The cell this container actually serves: its own variation's assignment,
  // falling back to the org-wide one, which is what serving resolves too.
  const assignment = placementId
    ? (assignments.data?.assignments.find(
        (a) => a.placementId === placementId && a.variationId === variationId
      ) ??
      assignments.data?.assignments.find(
        (a) => a.placementId === placementId && a.variationId === null
      ))
    : undefined;

  return (
    <Grid container spacing={3}>
      <Grid size={12}>
        <IgAlert
          alertKey={RULES_MOVED_ALERT_ID}
          severity="info"
          message="Rules have moved — they now live under Store management, so you can reuse one across experiences."
          isDismissed={isRulesMovedAlertDismissed}
          onDismiss={() => dismissAlert(RULES_MOVED_ALERT_ID)}
        />
      </Grid>
      <Grid container size={12} direction={"column"} spacing={1}>
        <IgTypography variant="body1" fontWeight={600}>
          Rule
        </IgTypography>
        <IgTypography variant="body2" color="text.secondary">
          Choose the Agentic Logic rule that selects products for this
          placement.
        </IgTypography>
      </Grid>
      <Grid size={12}>
        <Stack direction="row" alignItems="flex-end" spacing={1}>
          <IgSelect
            value={isError ? NONE : selected}
            disabled={isLoading || isError}
            onChange={(e) => {
              if (e.target.value === CREATE_RULE_OPTION) {
                onCreateRule?.();
                return;
              }
              onRuleChange(e.target.value);
            }}
            sx={{ width: 320 }}
          >
            {selectedIsUnavailable && (
              <MenuItem value={selected}>
                Currently attached rule (unavailable)
              </MenuItem>
            )}
            {selectableRules.map((rule) => (
              <MenuItem value={rule.id} key={rule.id}>
                {rule.name}
              </MenuItem>
            ))}
            {onCreateRule ? (
              <MenuItem value={CREATE_RULE_OPTION}>+ New rule</MenuItem>
            ) : null}
          </IgSelect>
          {onEditRule && selected !== NONE && !isError ? (
            <IgButton
              variant="text"
              onClick={() => onEditRule(selected)}
              sx={{ minHeight: 40 }}
            >
              Edit
            </IgButton>
          ) : null}
        </Stack>
        {!isLoading &&
          !isError &&
          selectableRules.length === 0 &&
          !selectedIsUnavailable && (
            <IgTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {" "}
              No rules are written for this placement yet.
            </IgTypography>
          )}
      </Grid>
      {selectedRuleSummary && !isError && (
        <Grid container size={12} direction={"column"} spacing={1}>
          <IgTypography variant="body1" fontWeight={600}>
            Rule summary
          </IgTypography>
          <IgTypography variant="body2" color="text.secondary">
            {selectedRuleSummary}
          </IgTypography>
        </Grid>
      )}
      {isError && (
        <Grid size={12}>
          <IgTypography variant="body2" color="error">
            Could not load rules. The placement keeps its current rule until
            this loads.
          </IgTypography>
        </Grid>
      )}
    </Grid>
  );
};
